import { useState, useEffect, useMemo } from 'react'
import { useRoleAccess } from '@/hooks/useRoleAccess'
import {
  monitoringService,
  DailySummary,
  TrendsResponse,
  periodicCollectionApi,
  PeriodicCollectionStatus,
  behaviorLearningApi,
  BehaviorLearningStatus,
  CredentialsUpdatedResponse,
} from '@/services/monitoring'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Loader2, TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Activity, Play, Pause, Zap, ZapOff, KeyRound } from 'lucide-react'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

/**
 * Format elapsed time since a given date in XdYhZm format
 */
function formatElapsedTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()

  if (diffMs < 0) return '0m'

  const totalMinutes = Math.floor(diffMs / (1000 * 60))
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60

  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`)

  return parts.join(' ')
}

export function DailyHealthDashboard() {
  const { hasRole } = useRoleAccess()
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState<DailySummary | null>(null)
  const [trends, setTrends] = useState<TrendsResponse | null>(null)
  const [trendDays, setTrendDays] = useState<7 | 30>(7)
  const [error, setError] = useState<string | null>(null)

  // Periodic collection toggle state
  const [periodicEnabled, setPeriodicEnabled] = useState(true)
  const [periodicStatus, setPeriodicStatus] = useState<PeriodicCollectionStatus | null>(null)
  const [periodicLoading, setPeriodicLoading] = useState(false)
  const [showPauseDialog, setShowPauseDialog] = useState(false)

  // Behavior learning toggle state
  const [learningEnabled, setLearningEnabled] = useState(true)
  const [learningStatus, setLearningStatus] = useState<BehaviorLearningStatus | null>(null)
  const [learningLoading, setLearningLoading] = useState(false)
  const [showDisableDialog, setShowDisableDialog] = useState(false)

  // Credentials updated state
  const [credentialsUpdated, setCredentialsUpdated] = useState<CredentialsUpdatedResponse | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch both summary and trends in parallel
        const [summaryData, trendsData] = await Promise.all([
          monitoringService.getDailySummary(),
          monitoringService.getTrends(trendDays)
        ])

        setSummary(summaryData)
        setTrends(trendsData)
      } catch (err: any) {
        console.error('Failed to fetch daily health data:', err)
        setError(err.response?.data?.detail || 'Failed to load dashboard data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    // Poll every 30 seconds
    const interval = setInterval(fetchData, 30000)

    return () => clearInterval(interval)
  }, [trendDays])

  // Fetch toggle statuses on mount
  useEffect(() => {
    fetchPeriodicStatus()
    fetchLearningStatus()
    fetchCredentialsUpdated()
  }, [])

  // Fetch credentials updated count
  const fetchCredentialsUpdated = async () => {
    try {
      const data = await monitoringService.getCredentialsUpdatedCount('2026-01-06')
      setCredentialsUpdated(data)
    } catch (error) {
      console.error('Failed to fetch credentials updated count:', error)
    }
  }

  // Periodic collection handlers
  const fetchPeriodicStatus = async () => {
    try {
      const status = await periodicCollectionApi.getStatus()
      setPeriodicStatus(status)
      setPeriodicEnabled(!status.paused)
    } catch (error) {
      console.error('Failed to fetch periodic collection status:', error)
      setPeriodicEnabled(true)
    }
  }

  const handlePeriodicToggle = (checked: boolean) => {
    if (!checked) {
      setShowPauseDialog(true)
    } else {
      handlePeriodicResume()
    }
  }

  const handlePeriodicPauseConfirm = async () => {
    setPeriodicLoading(true)
    try {
      const status = await periodicCollectionApi.pause({
        reason: 'Manual pause from monitoring UI',
        paused_by: 'admin'
      })
      setPeriodicStatus(status)
      setPeriodicEnabled(false)
    } catch (error) {
      console.error('Failed to pause periodic collection:', error)
    } finally {
      setPeriodicLoading(false)
      setShowPauseDialog(false)
    }
  }

  const handlePeriodicResume = async () => {
    setPeriodicLoading(true)
    try {
      const status = await periodicCollectionApi.resume()
      setPeriodicStatus(status)
      setPeriodicEnabled(true)
    } catch (error) {
      console.error('Failed to resume periodic collection:', error)
    } finally {
      setPeriodicLoading(false)
    }
  }

  // Behavior learning handlers
  const fetchLearningStatus = async () => {
    try {
      const status = await behaviorLearningApi.getStatus()
      setLearningStatus(status)
      setLearningEnabled(status.enabled)
    } catch (error) {
      console.error('Failed to fetch behavior learning status:', error)
      setLearningEnabled(true)
    }
  }

  const handleLearningToggle = (checked: boolean) => {
    if (!checked) {
      setShowDisableDialog(true)
    } else {
      handleEnableLearning()
    }
  }

  const handleDisableConfirm = async () => {
    setLearningLoading(true)
    try {
      const status = await behaviorLearningApi.disable('Manual disable from monitoring UI')
      setLearningStatus(status)
      setLearningEnabled(false)
    } catch (error) {
      console.error('Failed to disable behavior learning:', error)
    } finally {
      setLearningLoading(false)
      setShowDisableDialog(false)
    }
  }

  const handleEnableLearning = async () => {
    setLearningLoading(true)
    try {
      const status = await behaviorLearningApi.enable()
      setLearningStatus(status)
      setLearningEnabled(true)
    } catch (error) {
      console.error('Failed to enable behavior learning:', error)
    } finally {
      setLearningLoading(false)
    }
  }

  // Normalize trends data for 100% stacked bar chart (must be before early returns)
  const normalizedTrends = useMemo(() => {
    if (!trends?.trends) return []
    return trends.trends.map(day => {
      const total = day.total_jobs || 1 // Avoid division by zero
      return {
        ...day,
        success_pct: (day.successful_jobs / total) * 100,
        failed_pct: (day.failed_jobs / total) * 100,
      }
    })
  }, [trends])

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-lg font-semibold">Failed to load dashboard</p>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </div>
      </div>
    )
  }

  if (!summary || !trends) {
    return null
  }

  const successRate = summary.summary.success_rate
  const isHealthy = successRate >= 90

  return (
    <div className="space-y-6">
      {/* Header with Date and Status */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-semibold">Daily Health Dashboard</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Showing data for {new Date(summary.date).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} (UTC)
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* System Toggles - Admin only */}
          {hasRole('admin') && (
            <div className="flex items-center gap-4 border-r pr-4">
              {/* Periodic Collection Toggle */}
              <div className="flex items-center gap-2">
                {periodicEnabled ? (
                  <Play className="h-4 w-4 text-green-500" />
                ) : (
                  <Pause className="h-4 w-4 text-yellow-500" />
                )}
                <Label htmlFor="periodic-toggle" className="text-sm font-medium cursor-pointer">
                  Periodic
                </Label>
                <Switch
                  id="periodic-toggle"
                  checked={periodicEnabled}
                  onCheckedChange={handlePeriodicToggle}
                  disabled={periodicLoading}
                />
                {periodicStatus?.paused && periodicStatus?.paused_at && (
                  <span className="text-xs text-muted-foreground">
                    (paused {formatElapsedTime(new Date(periodicStatus.paused_at))} ago)
                  </span>
                )}
              </div>

              {/* Behavior Learning Toggle */}
              <div className="flex items-center gap-2">
                {learningEnabled ? (
                  <Zap className="h-4 w-4 text-green-500" />
                ) : (
                  <ZapOff className="h-4 w-4 text-red-500" />
                )}
                <Label htmlFor="learning-toggle" className="text-sm font-medium cursor-pointer">
                  Behavior Triggered
                </Label>
                <Switch
                  id="learning-toggle"
                  checked={learningEnabled}
                  onCheckedChange={handleLearningToggle}
                  disabled={learningLoading}
                />
                {!learningStatus?.enabled && learningStatus?.disabled_at && (
                  <span className="text-xs text-muted-foreground">
                    (disabled {formatElapsedTime(new Date(learningStatus.disabled_at))} ago)
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Health Status Badge */}
          {isHealthy ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              Healthy
            </Badge>
          ) : successRate >= 80 ? (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              <AlertCircle className="h-3.5 w-3.5 mr-1" />
              Degraded
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
              <AlertCircle className="h-3.5 w-3.5 mr-1" />
              Critical
            </Badge>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Jobs Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Jobs</CardDescription>
            <CardTitle className="text-3xl">{summary.summary.total_jobs.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {summary.summary.integrations_run} integrations • {summary.summary.organizations_affected} orgs
            </p>
          </CardContent>
        </Card>

        {/* Success Rate Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Success Rate</CardDescription>
            <CardTitle className="text-3xl flex items-baseline gap-2">
              {summary.summary.success_rate.toFixed(1)}%
              {successRate >= 90 ? (
                <TrendingUp className="h-5 w-5 text-green-600" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-600" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {summary.summary.successful_jobs} succeeded • {summary.summary.failed_jobs} failed
            </p>
          </CardContent>
        </Card>

        {/* Records Processed Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Records Processed</CardDescription>
            <CardTitle className="text-3xl">
              {summary.data.total_records >= 1000000
                ? `${(summary.data.total_records / 1000000).toFixed(1)}M`
                : summary.data.total_records >= 1000
                ? `${(summary.data.total_records / 1000).toFixed(1)}K`
                : summary.data.total_records.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {summary.periods.total} periods completed
            </p>
          </CardContent>
        </Card>

        {/* Avg Duration Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Period Duration</CardDescription>
            <CardTitle className="text-3xl">
              {summary.data.avg_period_duration_seconds >= 60
                ? `${Math.floor(summary.data.avg_period_duration_seconds / 60)}m`
                : `${summary.data.avg_period_duration_seconds.toFixed(0)}s`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Period success rate: {summary.periods.success_rate.toFixed(1)}%
            </p>
          </CardContent>
        </Card>

        {/* Credentials Updated Card */}
        {credentialsUpdated && (
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" />
                Credentials Since Jan 06, 2026
              </CardDescription>
              <CardTitle className="text-3xl">{credentialsUpdated.count.toLocaleString()}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                {credentialsUpdated.breakdown.new} new • {credentialsUpdated.breakdown.updated} updated
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Trend Charts */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Trends
          </h4>
          <div className="flex gap-2">
            <button
              onClick={() => setTrendDays(7)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                trendDays === 7
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setTrendDays(30)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                trendDays === 30
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              30 Days
            </button>
          </div>
        </div>

        {/* Success Rate Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Success Rate Trend</CardTitle>
            <CardDescription>Daily success rate over the last {trendDays} days</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trends.trends}>
                <defs>
                  <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}
                  className="text-xs"
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  className="text-xs"
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-background border rounded-lg p-3 shadow-lg">
                          <p className="text-sm font-medium mb-2">
                            {new Date(payload[0].payload.date).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })}
                          </p>
                          <p className="text-sm text-green-600">
                            Success Rate: {payload[0].payload.success_rate.toFixed(1)}%
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {payload[0].payload.successful_jobs} / {payload[0].payload.total_jobs} jobs
                          </p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="success_rate"
                  stroke="#22c55e"
                  strokeWidth={2}
                  fill="url(#successGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Job Volume Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Job Volume Trend</CardTitle>
            <CardDescription>Success vs failure ratio over the last {trendDays} days</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={normalizedTrends}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}
                  className="text-xs"
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                  className="text-xs"
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload
                      return (
                        <div className="bg-background border rounded-lg p-3 shadow-lg">
                          <p className="text-sm font-medium mb-2">
                            {new Date(data.date).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })}
                          </p>
                          <p className="text-sm text-green-600">
                            Successful: {data.successful_jobs} ({data.success_pct.toFixed(1)}%)
                          </p>
                          <p className="text-sm text-red-600">
                            Failed: {data.failed_jobs} ({data.failed_pct.toFixed(1)}%)
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Total: {data.total_jobs} jobs
                          </p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Legend />
                <Bar dataKey="success_pct" name="Successful" fill="#22c55e" stackId="jobs" />
                <Bar dataKey="failed_pct" name="Failed" fill="#ef4444" stackId="jobs" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      {/* Pause Periodic Collection Confirmation Dialog */}
      <AlertDialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pause Periodic Collection?</AlertDialogTitle>
            <AlertDialogDescription>
              This will pause the hourly periodic collection for all integrations.
              No new periodic jobs will be created until you resume.
              <br /><br />
              <strong>Note:</strong> Already running jobs will continue to completion.
              Backfill and reprocess jobs are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={periodicLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePeriodicPauseConfirm}
              disabled={periodicLoading}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {periodicLoading ? 'Pausing...' : 'Pause Collection'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disable Behavior Learning Confirmation Dialog */}
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Behavior Learning?</AlertDialogTitle>
            <AlertDialogDescription>
              This will activate the <strong>kill switch</strong> and revert all integrations to hourly collection.
              Use this only in emergency situations when the behavior learning system is causing issues.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={learningLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisableConfirm}
              disabled={learningLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {learningLoading ? 'Disabling...' : 'Disable Learning'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
